import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AppDataService, OperatorService, RiderService } from 'app/services';
import { AppComponent } from 'app/app.component';
import { MessageService } from 'primeng/api';
import { OperatorVehicleModel, BoundaryFeeModel } from 'app/models';
import { DomSanitizer } from '@angular/platform-browser';
import { OperatorVehicleOwnershipDocumentsModel } from 'app/models/operator/operator-vehicle-ownership-documents.model';
import { Utils } from 'app/utils';
import { XpressDocumentTypeEnum } from '@shared/enums/xpress-document-type.enum';
import * as moment from 'moment';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
    clearOperatorVehicleStagedUploads,
    fileBrowseHandlerForOperatorVehicle,
    flushOperatorVehicleDocumentDeletes,
    flushStagedOperatorVehicleUploads,
    getOperatorVehicleUploadedFiles,
    hasOperatorVehicleMaxFilesUploaded,
    hasOperatorVehiclePdfUploaded,
    hasOperatorVehicleStagedUploads,
    isPdfDocumentFileUrl,
    normalizeOperatorVehiclePlate,
    OPERATOR_VEHICLE_ACCEPTED_FILE_INPUT,
    OPERATOR_VEHICLE_MULTI_FILE_FIELDS,
    OperatorVehicleUploadField,
    removeOperatorVehicleDocumentFileLocally,
    UploadedDocumentMeta,
    UploadedDocumentMetaMap
} from '../shared/operator-vehicle-document-upload';

enum MenuTab {
    Insights = 1,
    LegalDocs = 2,
    Drivers = 3,
    BoundaryFee = 4
}

enum DocsMenuTab {
    VehicleDocs = 1,
    TNVSDocs = 2
}

interface LazyLoadParams {
    first?: number;
    rows?: number;
    sortField?: string | string[];
    sortOrder?: number;
}

@Component({
    selector: 'operator-vehicle-detail',
    templateUrl: './operator-vehicle-detail.component.html',
    styleUrls: ['./operator-vehicle-detail.component.scss',
        '../../../../../../styles/menu-sidebar.scss',
        '../../../../../../styles/style-layout.scss'
    ],
})
export class OperatorVehicleDetailComponent implements OnInit {
    @Input()
    public vehicleId!: number;

    @Output() changedAddOperatorVehicleDetailEmit = new EventEmitter<boolean>();

    constructor(private messageService: MessageService, public appData: AppDataService,
        public app: AppComponent, private operatorService: OperatorService, private riderService: RiderService,
        private sanitizer: DomSanitizer, breakpointObserver: BreakpointObserver) {
        breakpointObserver.observe('(max-width: 560px)')
            .pipe(takeUntilDestroyed())
            .subscribe(state => this.visibleThumbnails = state.matches ? 3 : 4);
    }

    public menuTab = MenuTab;
    public activeTab: MenuTab = MenuTab.Insights;

    private readonly tabMenuMap: MenuTab[] = [
        MenuTab.Insights,
        MenuTab.LegalDocs,
        MenuTab.Drivers,
        MenuTab.BoundaryFee
    ];

    // public docsMenuTab = DocsMenuTab;
    public activeDocTab: DocsMenuTab = DocsMenuTab.VehicleDocs;

    public discardConfirmDialog: boolean = false;
    ltooProgressBar: number = 0;
    certifProgressBar: number = 0;
    provProgressBar: number = 0;

    ltooInterval: any;
    certifInterval: any;
    provInterval: any;

    public isLoading: boolean = true;

    public operatorVehicle!: any;
    public legalDocsImages: any[] = [];

    public visibleAddDriversToVhicle: boolean = false;

    //start galleria
    public galleryVehicleItems: any = [];
    public galleryItems: any = [];
    public activeGalleryImgIndex: number = 0;
    public displayImg: boolean = false;
    public vehicleHeroImageUrl: string | null = null;
    public visibleThumbnails: number = 4;
    //end galleria

    //Start Grid settings
    public driverCols: any[] = [
        { field: 'RiderId', header: 'Driver ID' },
        { field: 'UserName', header: 'DriverName' },
        { field: 'Phone', header: 'Phone No.' }
    ];
    public displayedColumns: string[] = ['RiderId', 'UserName', 'Phone', 'actions'];

    public rowsPerPage: number = 15;
    public rowsPerPageOptions: Array<number> = [15, 50, 100, 500];
    public totalRecords: number = -1;
    public totalEarningsStr: string = '';

    public first: number = 0;
    public pageIndex: number = 0;
    public sortField: string | string[] = '';
    private sortOrder: number = 1;

    public drivers: Array<any> = [];
    //End Grid settings

    private searchTimer: number = 0;
    public filter: any = {
        search: null
    };

    public visibleEditBoundaryFeeDialog: boolean = false;
    public showDeactivationDialog: boolean = false;

    public showVehicleDocs: boolean = false;
    public operatorVehicleOwnershipDocumentsModel: OperatorVehicleOwnershipDocumentsModel = new OperatorVehicleOwnershipDocumentsModel();
    public initialDocModel: OperatorVehicleOwnershipDocumentsModel = new OperatorVehicleOwnershipDocumentsModel();

    public showValidationError: boolean = false;
    public minDate: Date = new Date();
    public operatorXpressUserId: number = 0;

    private uploadedDocumentMeta: UploadedDocumentMetaMap = {};
    private initialUploadedFileIds: number[] = [];
    /** Removals staged in the dialog; persisted only on Submit. */
    private pendingDocumentDeletes: Array<Pick<UploadedDocumentMeta, 'id' | 'type'>> = [];
    public readonly acceptedVehicleDocTypes = OPERATOR_VEHICLE_ACCEPTED_FILE_INPUT;
    public readonly multiFileDocFields = OPERATOR_VEHICLE_MULTI_FILE_FIELDS;

    ngOnInit(): void {
        this.getOperatorVehicle();
    }

    public handleTabChange(index: number): void {
        this.activeTab = this.tabMenuMap[index] ?? MenuTab.Insights;
        if (this.activeTab === MenuTab.Drivers) {
            this.getRidersByVehicle(0);
        }
    }

    private getOperatorVehicle() {
        // Keep existing vehicle data visible during refetch so a failed reload
        // does not render the detail template against an undefined model.
        const isInitialLoad = !this.operatorVehicle;
        if (isInitialLoad) {
            this.isLoading = true;
        }

        const dto = {
            VehicleId: this.vehicleId,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.operatorService.getOperatorVehicle(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.isLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }

                this.operatorVehicle = new OperatorVehicleModel(data);
                if (this.getApprovalStatus() !== 'Approved') {
                    this.operatorVehicle.IsActive = false;
                }
                this.operatorXpressUserId = data.OperatorXpressUserId || parseInt(this.appData.authData.UserId, 10) || 0;

                this.addArrayVehicleImgages(data.VehiclePhotoUrls || []);

                this.operatorVehicleOwnershipDocumentsModel.IsOwner = this.operatorVehicle.IsOwner;
                this.operatorVehicleOwnershipDocumentsModel.VehicleId = this.operatorVehicle.Id;
                this.operatorVehicleOwnershipDocumentsModel.PaOrCpcCaseNumber = this.operatorVehicle.PaOrCpcCaseNumber;
                this.hydrateDocumentsModelFromResponse(data.Documents || []);
                this.buildLegalDocsGallery(data.Documents || []);

                this.initialDocModel = JSON.parse(JSON.stringify(this.operatorVehicleOwnershipDocumentsModel));
                this.initialUploadedFileIds = this.getUploadedFileIdsSnapshot();
                this.pendingDocumentDeletes = [];
                this.isLoading = false;
            })
            .catch(err => {
                this.isLoading = false;
                throw err;
            });

        this.getRidersByVehicle(0);
    }

    private activeOrInactiveVehicle(isActive: boolean) {
        const dto = {
            VehicleId: this.vehicleId,
            IsActive: isActive
        };

        this.operatorService.activeOrInactiveVehicle(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.isLoading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Vehicle updated successfully' });
            })
            .catch(err => {
                this.isLoading = false;
                throw err;
            });
    }

    public isPdf(url: string): Promise<boolean> {
        if (url) {
            const dto = { Url: url };

            return this.riderService.getContentTypeByImageUrl(dto, this.messageService)
                .then(data => {
                    if (!data.Success) {
                        console.log(data.ErrorMessage);
                        return false;
                    }
                    return data.ContentType?.toLowerCase().includes('application/pdf') ?? false;
                })
                .catch(err => {
                    console.log(err);
                    return false;
                });
        }
        return Promise.resolve(false);
    }

    getSafeUrl(url: string) {
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    public addArrayVehicleImgages(images: string[]) {
        this.galleryVehicleItems = [];
        images.forEach((img: string) => {
            if (img) {
                this.galleryVehicleItems.push({ value: img });
            }
        });
        this.vehicleHeroImageUrl = this.galleryVehicleItems[0]?.value
            || this.operatorVehicle?.VehicleFrontImageUrl
            || null;
    }

    public discardChanges() {
        this.discardConfirmDialog = true;
        this.activeDocTab = DocsMenuTab.VehicleDocs;
    }

    public cancelDiscard() {
        this.discardConfirmDialog = false;
        this.activeDocTab = DocsMenuTab.VehicleDocs;
    }

    public discardConfirm() {
        // Browse stages files locally; deletes are also staged until Submit.
        // Discard drops local staged files and restores server state via refetch.
        clearOperatorVehicleStagedUploads(
            this.operatorVehicleOwnershipDocumentsModel,
            this.uploadedDocumentMeta
        );
        this.pendingDocumentDeletes = [];
        this.discardConfirmDialog = false;
        this.showVehicleDocs = false;
        this.getOperatorVehicle();
    }

    public showVisibleImageModal(images: any[], indexImg: number) {
        this.galleryItems = images;
        this.activeGalleryImgIndex = indexImg;
        this.displayImg = true;
    }

    public canAssignDrivers(): boolean {
        return this.getApprovalStatus() === 'Approved';
    }

    public openAddDriversDialog() {
        if (!this.canAssignDrivers()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Drivers can only be assigned to approved vehicles.'
            });
            return;
        }
        this.visibleAddDriversToVhicle = true;
    }

    changedVisibleAddDriversToVhicleEmit(value: boolean) {
        this.visibleAddDriversToVhicle = value;
        this.pageIndex = 0;
        this.getRidersByVehicle(0);
    }

    public onPageChange(event: PageEvent): void {
        this.rowsPerPage = event.pageSize;
        this.pageIndex = event.pageIndex;
        this.getRidersByVehicle(event.pageIndex * event.pageSize);
    }

    public onSortChange(sort: Sort): void {
        this.sortField = sort.active;
        this.sortOrder = sort.direction === 'desc' ? -1 : 1;
        this.pageIndex = 0;
        this.getRidersByVehicle(0);
    }

    /** @deprecated Use onPageChange / onSortChange for mat-table; kept for internal TS calls */
    private handleLazyLoad(params: LazyLoadParams): void {
        const skip = params.first ?? 0;
        if (params.rows) {
            this.rowsPerPage = params.rows;
        }
        if (!params.sortField) {
            this.sortField = 'Id';
            this.sortOrder = -1;
        } else {
            this.sortField = params.sortField;
            this.sortOrder = params.sortOrder ?? 1;
        }
        this.getRidersByVehicle(skip);
    }

    public getRidersByVehicle(skip: number) {
        const dto = {
            VehicleId: this.vehicleId,
            Search: (this.filter.search) ? this.filter.search : null,
            Skip: skip,
            Take: this.rowsPerPage,
            SortField: this.sortField,
            SortOrder: this.sortOrder
        };

        this.riderService.getRidersByVehicle(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    console.log(data.ErrorMessage);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.first = skip;
                this.totalRecords = data.TotalRecords;
                this.drivers = data.Items;
            });
    }

    public removeRiderFromOperatorVehicle(riderId: number) {
        const dto = {
            VehicleId: this.vehicleId,
            RiderId: riderId
        };

        this.riderService.removeRiderFromOperatorVehicle(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    console.log(data.ErrorMessage);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.pageIndex = 0;
                this.getRidersByVehicle(0);
            });
    }

    public handleFilterChange(): void {
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            this.pageIndex = 0;
            this.getRidersByVehicle(0);
            this.searchTimer = 0;
        }, 1500);
    }

    public openEditBoundaryFeeDialog() {
        this.visibleEditBoundaryFeeDialog = true;
    }

    public changedVisibleEditBoundaryFeeDialogEmit(value: boolean) {
        this.visibleEditBoundaryFeeDialog = value;
    }

    public saveBoundaryFee(boundaryFee: BoundaryFeeModel) {
        const dto = {
            Id: boundaryFee.Id,
            BoundaryAmount: boundaryFee.BoundaryAmount,
            ProcessingFee: boundaryFee.BoundaryProcessingFee,
            AvailableDaysOfWeek: boundaryFee.BoundaryAvailableDays,
            AvailableFromHours: boundaryFee.BoundaryAvailableFromHours,
            AvailableFromMins: boundaryFee.BoundaryAvailableFromMins,
            AvailableToHours: boundaryFee.BoundaryAvailableToHours,
            AvailableToMins: boundaryFee.BoundaryAvailableToMins,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.operatorService.updateVehicleOperatorSchedule(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    console.log(data.ErrorMessage);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Boundary Fee saved successfuly' });
                this.getOperatorVehicle();
                this.changedVisibleEditBoundaryFeeDialogEmit(false);
            });
    }

    public backToOperatorVehicles() {
        this.changedAddOperatorVehicleDetailEmit.emit(false);
    }

    public getApprovalStatus(): 'Pending' | 'Approved' | 'Rejected' {
        const status = (this.operatorVehicle?.Status || '').trim();
        if (status === 'Approved') {
            return 'Approved';
        }
        if (status === 'Rejected') {
            return 'Rejected';
        }
        return 'Pending';
    }

    public getApprovalStatusIconSrc(): string | null {
        const status = this.getApprovalStatus();
        if (status === 'Pending') {
            return '../Content/xpress-app/images/operator/pending-review.svg';
        }
        if (status === 'Rejected') {
            return '../Content/xpress-app/images/operator/rejected.svg';
        }
        return null;
    }

    public getApprovalStatusLabel(): string {
        const status = this.getApprovalStatus();
        return status === 'Pending' ? 'Pending Review' : status;
    }

    public canToggleVehicleActive(): boolean {
        return this.getApprovalStatus() === 'Approved';
    }

    public onChangeSwitch(event: MatSlideToggleChange) {
        if (!this.canToggleVehicleActive()) {
            this.operatorVehicle.IsActive = false;
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Pending and rejected vehicles cannot be activated.'
            });
            return;
        }

        if (!this.operatorVehicle.IsActive) {
            this.operatorVehicle.IsActive = true;

            if (this.drivers.length > 0) {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Deactivation failed.' +
                        ' Please remove the driver assigned to this vehicle before proceeding.' });
                return;
            } else {
                this.showDeactivationDialog = true;
            }
        } else {
            this.activeOrInactiveVehicle(true);
        }
    }

    public cancelDeactivation() {
        this.showDeactivationDialog = false;
    }

    public deactivateVehicle() {
        this.activeOrInactiveVehicle(false);
        this.operatorVehicle.IsActive = false;
        this.showDeactivationDialog = false;
    }

    public updateDocuments() {
        const hasDocumentFileChanges = this.hasDocumentFileChanges();
        const pendingDeletes = [...this.pendingDocumentDeletes];
        const plate = normalizeOperatorVehiclePlate(this.operatorVehicle?.NumberPlate);

        const expiryItems: any[] = [];
        const model = this.operatorVehicleOwnershipDocumentsModel;
        if (model.LTOOfficialReceiptExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration],
                ExpiryDate: moment(model.LTOOfficialReceiptExpireAt).format('YYYY-MM-DD')
            });
        }
        if (model.CertificatePublicConvenienceExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Cpc],
                ExpiryDate: moment(model.CertificatePublicConvenienceExpireAt).format('YYYY-MM-DD')
            });
        }
        if (model.ProvisionalAuthorityExpireAt) {
            expiryItems.push({
                Types: [XpressDocumentTypeEnum.Pa],
                ExpiryDate: moment(model.ProvisionalAuthorityExpireAt).format('YYYY-MM-DD')
            });
        }

        // 1) Upload staged files  2) Flush staged deletes  3) Save ownership metadata / expiry
        flushStagedOperatorVehicleUploads(
            this.operatorService,
            this.messageService,
            this.operatorVehicleOwnershipDocumentsModel,
            this.uploadedDocumentMeta,
            this.operatorXpressUserId,
            plate
        )
            .then(uploadResult => {
                if (!uploadResult.Success) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: uploadResult.ErrorMessage || 'Failed to upload documents.'
                    });
                    return null;
                }

                if (uploadResult.UploadedCount > 0) {
                    // ImageService resets approval on ownership doc upload — mirror locally.
                    this.syncApprovalStateAfterPendingDocumentDeletes();
                }

                return flushOperatorVehicleDocumentDeletes(
                    this.operatorService,
                    this.messageService,
                    pendingDeletes,
                    this.operatorXpressUserId
                );
            })
            .then(deleteResult => {
                if (!deleteResult) {
                    return null;
                }
                if (!deleteResult.Success) {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: deleteResult.ErrorMessage || 'Failed to delete documents.'
                    });
                    // Partial deletes may already have succeeded and reset approval.
                    // Drop those from pending, sync local status, then refetch so the
                    // dialog matches server state (files restored or removed).
                    const deletedIds = new Set(deleteResult.DeletedIds ?? []);
                    if (deletedIds.size > 0) {
                        this.pendingDocumentDeletes = this.pendingDocumentDeletes
                            .filter(item => !deletedIds.has(item.id));
                        this.syncApprovalStateAfterPendingDocumentDeletes();
                    }
                    this.getOperatorVehicle();
                    return null;
                }

                this.pendingDocumentDeletes = [];
                if (pendingDeletes.length > 0) {
                    this.syncApprovalStateAfterPendingDocumentDeletes();
                }

                return this.operatorService.updateOperatorVehicleDocuments({
                    VehicleId: this.operatorVehicleOwnershipDocumentsModel.VehicleId,
                    IsOwner: this.operatorVehicleOwnershipDocumentsModel.IsOwner,
                    PaOrCpcCaseNumber: this.operatorVehicleOwnershipDocumentsModel.PaOrCpcCaseNumber,
                    HasNewDocumentUploads: hasDocumentFileChanges
                }, this.messageService);
            })
            .then(data => {
                if (!data) {
                    return null;
                }
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return null;
                }

                if (expiryItems.length === 0) {
                    return { Success: true };
                }

                if (!plate) {
                    return {
                        Success: false,
                        ErrorMessage: 'Vehicle plate number is required to save document expiry dates.'
                    };
                }

                return this.operatorService.updateDocumentExpiryDates({
                    XpressUserId: this.operatorXpressUserId > 0 ? this.operatorXpressUserId : null,
                    DocumentNumber: plate,
                    Items: expiryItems
                }, this.messageService);
            })
            .then(expiryResult => {
                if (!expiryResult) {
                    return;
                }
                if (!expiryResult.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: expiryResult.ErrorMessage });
                    return;
                }

                this.getOperatorVehicle();
                this.messageService.add({
                    severity: 'success',
                    summary: 'Vehicle docs updated successfully',
                    detail: hasDocumentFileChanges
                        ? 'Verification will be completed within 1-2 business days.'
                        : 'Vehicle document details were saved.'
                });
                this.showVehicleDocs = false;
                this.activeDocTab = DocsMenuTab.VehicleDocs;
            });
    }

    private hasDocumentFileChanges(): boolean {
        if (this.pendingDocumentDeletes.length > 0) {
            return true;
        }
        if (hasOperatorVehicleStagedUploads(this.uploadedDocumentMeta)) {
            return true;
        }
        const currentIds = this.getUploadedFileIdsSnapshot();
        if (currentIds.length !== this.initialUploadedFileIds.length) {
            return true;
        }
        const initialSet = new Set(this.initialUploadedFileIds);
        return currentIds.some(id => !initialSet.has(id));
    }

    private getUploadedFileIdsSnapshot(): number[] {
        return Object.values(this.uploadedDocumentMeta)
            .flatMap(files => files ?? [])
            .filter(file => !file.isLocal)
            .map(file => file.id)
            .sort((a, b) => a - b);
    }

    public getUploadedFiles(fieldName: OperatorVehicleUploadField): UploadedDocumentMeta[] {
        return getOperatorVehicleUploadedFiles(this.uploadedDocumentMeta, fieldName);
    }

    public hasPdfUploaded(fieldName: OperatorVehicleUploadField): boolean {
        return hasOperatorVehiclePdfUploaded(this.uploadedDocumentMeta, fieldName);
    }

    public canUploadMoreFiles(fieldName: OperatorVehicleUploadField): boolean {
        return !this.hasPdfUploaded(fieldName)
            && !hasOperatorVehicleMaxFilesUploaded(this.uploadedDocumentMeta, fieldName);
    }

    public isMultiFileDocField(fieldName: OperatorVehicleUploadField): boolean {
        return this.multiFileDocFields.has(fieldName);
    }

    public fileBrowseHandler(event: any, fieldName: OperatorVehicleUploadField) {
        fileBrowseHandlerForOperatorVehicle(
            event,
            fieldName,
            this.messageService,
            this.operatorVehicleOwnershipDocumentsModel,
            this.uploadedDocumentMeta,
            this.operatorVehicle?.NumberPlate,
            {
                onServerFileReplaced: (meta) => {
                    if (!this.pendingDocumentDeletes.some(item => item.id === meta.id)) {
                        this.pendingDocumentDeletes.push({ id: meta.id, type: meta.type });
                    }
                }
            }
        );
    }

    public deleteUpload(fieldName: OperatorVehicleUploadField, fileId?: number) {
        const files = this.getUploadedFiles(fieldName);
        const targetId = fileId ?? files[0]?.id;
        if (targetId == null) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Unable to delete: document id is missing.'
            });
            return;
        }

        const removed = removeOperatorVehicleDocumentFileLocally(
            this.operatorVehicleOwnershipDocumentsModel,
            this.uploadedDocumentMeta,
            fieldName,
            targetId
        );
        if (!removed) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Unable to delete: document id is missing.'
            });
            return;
        }

        // Local staged files were never uploaded — nothing to delete on Submit.
        if (removed.isLocal) {
            return;
        }

        if (!this.pendingDocumentDeletes.some(item => item.id === removed.id)) {
            this.pendingDocumentDeletes.push({ id: removed.id, type: removed.type });
        }
    }

    /**
     * ImageService resets Approved/Rejected vehicles to Pending (and deactivates)
     * when ownership docs are uploaded/deleted on Submit. Mirror that locally.
     */
    private syncApprovalStateAfterPendingDocumentDeletes(): void {
        if (!this.operatorVehicle) {
            return;
        }

        if (this.getApprovalStatus() === 'Pending') {
            this.operatorVehicle.IsActive = false;
            return;
        }

        this.operatorVehicle.Status = 'Pending';
        this.operatorVehicle.IsActive = false;
    }

    private hydrateDocumentsModelFromResponse(documents: any[]) {
        const model = this.operatorVehicleOwnershipDocumentsModel;
        model.LTOOfficialReceiptImageUrl = null as any;
        model.CertificateRegistrationImageUrl = null as any;
        model.SPAImageUrl = null as any;
        model.ProvisionalAuthorityImageUrl = null as any;
        model.CertificatePublicConvenienceImageUrl = null as any;
        model.LTOOfficialReceiptExpireAt = null as any;
        model.ProvisionalAuthorityExpireAt = null as any;
        model.CertificatePublicConvenienceExpireAt = null as any;
        this.uploadedDocumentMeta = {};

        const plate = normalizeOperatorVehiclePlate(this.operatorVehicle?.NumberPlate);
        const findDoc = (type: XpressDocumentTypeEnum) => documents.find((d: any) => d.Type === type);
        const sortFiles = (files: any[]) =>
            [...(files || [])]
                .filter((file: any) => !!file?.ImageUrl)
                .sort((a: any, b: any) => (a.FileId ?? 0) - (b.FileId ?? 0));

        // Blob URLs are GUID-only (no .pdf). OR/CR PDFs also use DocumentFileType 1/2 for slot,
        // not Pdf=5 — so resolve PDF via content-type before showing XpressDocument.Name.
        const toMetaAsync = (
            file: any,
            type: XpressDocumentTypeEnum,
            documentName?: string
        ): Promise<UploadedDocumentMeta> => {
            const quickPdf = isPdfDocumentFileUrl(file.ImageUrl, file.DocumentFileType)
                || !!file.FileName;
            const resolve = (isPdf: boolean): UploadedDocumentMeta => ({
                id: file.FileId,
                type,
                url: file.ImageUrl,
                // Prefer per-file FileName from API; fall back to document Name for PDFs.
                fileName: isPdf ? (file.FileName || documentName || '') : '',
                isPdf,
                documentNumber: plate || undefined
            });

            if (quickPdf) {
                return Promise.resolve(resolve(true));
            }

            return this.isPdf(file.ImageUrl).then(resolve);
        };

        const setOwnershipImageUrl = (field: OperatorVehicleUploadField, url: string) => {
            switch (field) {
                case 'LTOOfficialReceiptImageUrl':
                    model.LTOOfficialReceiptImageUrl = url;
                    break;
                case 'CertificateRegistrationImageUrl':
                    model.CertificateRegistrationImageUrl = url;
                    break;
                case 'SPAImageUrl':
                    model.SPAImageUrl = url;
                    break;
                case 'ProvisionalAuthorityImageUrl':
                    model.ProvisionalAuthorityImageUrl = url;
                    break;
                case 'CertificatePublicConvenienceImageUrl':
                    model.CertificatePublicConvenienceImageUrl = url;
                    break;
                default:
                    break;
            }
        };

        const assignField = (
            field: OperatorVehicleUploadField,
            files: any[],
            type: XpressDocumentTypeEnum,
            documentName?: string
        ) => {
            if (files.length === 0) {
                return;
            }
            setOwnershipImageUrl(field, files[0].ImageUrl);
            void Promise.all(files.map((f: any) => toMetaAsync(f, type, documentName)))
                .then(metas => {
                    this.uploadedDocumentMeta[field] = metas;
                });
        };

        const orCr = findDoc(XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration);
        if (orCr) {
            const files = sortFiles(orCr.Files);
            const typedOr = files.filter((f: any) => Number(f.DocumentFileType) === 1);
            const typedCr = files.filter((f: any) => Number(f.DocumentFileType) === 2);
            const untyped = files.filter((f: any) =>
                Number(f.DocumentFileType) !== 1 && Number(f.DocumentFileType) !== 2);

            let orFiles = typedOr;
            let crFiles = typedCr;

            // Legacy untyped files only: first = OR, rest = CR (matches gallery labeling).
            if (orFiles.length === 0 && crFiles.length === 0 && untyped.length > 0) {
                orFiles = [untyped[0]];
                crFiles = untyped.slice(1);
            } else if (untyped.length > 0) {
                // Prefer typed roles; leave untyped attached to OR for visibility.
                orFiles = [...orFiles, ...untyped];
            }

            assignField('LTOOfficialReceiptImageUrl', orFiles, orCr.Type, orCr.Name);
            assignField('CertificateRegistrationImageUrl', crFiles, orCr.Type, orCr.Name);
            if (orCr.ExpiryDate) {
                model.LTOOfficialReceiptExpireAt = new Date(orCr.ExpiryDate);
            }
        }

        const spa = findDoc(XpressDocumentTypeEnum.NotarizedSPA);
        if (spa) {
            assignField('SPAImageUrl', sortFiles(spa.Files), spa.Type, spa.Name);
        }

        const pa = findDoc(XpressDocumentTypeEnum.Pa) || findDoc(XpressDocumentTypeEnum.PaOrCpc);
        if (pa) {
            assignField('ProvisionalAuthorityImageUrl', sortFiles(pa.Files), pa.Type, pa.Name);
            if (pa.ExpiryDate) {
                model.ProvisionalAuthorityExpireAt = new Date(pa.ExpiryDate);
            }
        }

        const cpc = findDoc(XpressDocumentTypeEnum.Cpc);
        if (cpc) {
            assignField('CertificatePublicConvenienceImageUrl', sortFiles(cpc.Files), cpc.Type, cpc.Name);
            if (cpc.ExpiryDate) {
                model.CertificatePublicConvenienceExpireAt = new Date(cpc.ExpiryDate);
            }
        }
    }

    private buildLegalDocsGallery(documents: any[]) {
        const titles: Record<number, string> = {
            [XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration]: 'OR/CR',
            [XpressDocumentTypeEnum.NotarizedSPA]: 'SPA',
            [XpressDocumentTypeEnum.Pa]: 'PA Document',
            [XpressDocumentTypeEnum.Cpc]: 'CPC Document',
            [XpressDocumentTypeEnum.PaOrCpc]: 'PA/CPC'
        };

        const pendingItems: Array<{
            imageUrl: string;
            expirationDate: any;
            title: string;
            documentName: string;
            fileNameHint: string;
            quickPdf: boolean;
        }> = [];

        documents
            .filter((d: any) => d.Type !== XpressDocumentTypeEnum.VehiclePhoto)
            .forEach((doc: any) => {
                const files = [...(doc.Files || [])]
                    .filter((file: any) => !!file?.ImageUrl)
                    .sort((a: any, b: any) => {
                        const typeDiff = (Number(a.DocumentFileType) || 99) - (Number(b.DocumentFileType) || 99);
                        return typeDiff !== 0 ? typeDiff : (a.FileId ?? 0) - (b.FileId ?? 0);
                    });

                files.forEach((file: any) => {
                    let title = titles[doc.Type] || 'Document';
                    let expirationDate = doc.ExpiryDate;
                    if (doc.Type === XpressDocumentTypeEnum.OfficialReceiptAndCertificateOfRegistration) {
                        if (Number(file.DocumentFileType) === 1) {
                            title = 'LTO Official Receipt (OR)';
                        } else if (Number(file.DocumentFileType) === 2) {
                            title = 'Certificate of Registration (CR)';
                            expirationDate = null; // CR has no expiration
                        } else if (files.length > 1) {
                            const isOr = file.FileId === files[0].FileId;
                            title = isOr
                                ? 'LTO Official Receipt (OR)'
                                : 'Certificate of Registration (CR)';
                            if (!isOr) {
                                expirationDate = null;
                            }
                        }
                    }

                    pendingItems.push({
                        imageUrl: file.ImageUrl,
                        expirationDate,
                        title,
                        documentName: doc.Name || '',
                        fileNameHint: file.FileName || '',
                        quickPdf: isPdfDocumentFileUrl(file.ImageUrl, file.DocumentFileType)
                            || !!file.FileName
                    });
                });
            });

        Promise.all(pendingItems.map(item => {
            const resolve = (isPdf: boolean) => ({
                isPdf,
                // Safe URL for full-screen PDF iframe; images use the raw URL.
                value: isPdf ? this.getSafeUrl(item.imageUrl) : item.imageUrl,
                expirationDate: item.expirationDate,
                title: item.title,
                fileName: isPdf ? (item.fileNameHint || item.documentName || '') : ''
            });

            if (item.quickPdf) {
                return Promise.resolve(resolve(true));
            }

            return this.isPdf(item.imageUrl).then(resolve);
        })).then(items => {
            this.legalDocsImages = items;
        });
    }

    protected readonly DocsMenuTab = DocsMenuTab;
    protected readonly Utils = Utils;
}
